import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Stack,
  Step,
  StepLabel,
  Stepper,
  OutlinedInput,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider,
  InputAdornment,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import * as SC from "SC";
import { httpService } from "api/httpService";

// ─── Plan definitions ─────────────────────────────────────────────────────────

const PLANS = [
  {
    id: "starter",
    label: "Starter",
    price: "$99",
    period: "/month",
    features: [
      "Up to 5 vehicles",
      "Basic dispatch management",
      "Driver tracking",
      "Email support",
    ],
  },
  {
    id: "professional",
    label: "Professional",
    price: "$199",
    period: "/month",
    features: [
      "Up to 25 vehicles",
      "Advanced dispatch & routing",
      "Real-time GPS tracking",
      "Client portal",
      "Priority support",
    ],
    popular: true,
  },
  {
    id: "enterprise",
    label: "Enterprise",
    price: "$299",
    period: "/month",
    features: [
      "Unlimited vehicles",
      "White-label branding",
      "Advanced analytics",
      "HIPAA compliance tools",
      "Dedicated account manager",
      "24/7 phone support",
    ],
  },
];

const STEPS = ["Company Setup", "Admin Account", "Payment"];

// ─── Utility ──────────────────────────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_URL || "https://api-dev.kericocare.com";

const checkSubdomainAvailability = async (subdomain) => {
  const clean = subdomain.toLowerCase().replace(/[^a-z0-9-]/g, "");
  if (!clean) return { available: null, error: null };
  try {
    const res = await fetch(
      `${API_BASE}/api/v1/tenants/check-subdomain?subdomain=${encodeURIComponent(clean)}`
    );
    const data = await res.json();
    return { available: data.available, error: null };
  } catch {
    return { available: null, error: "Could not check availability" };
  }
};

const createCheckoutSession = async ({ plan, token }) => {
  const result = await httpService({
    url: "/api/v1/billing/checkout-session",
    options: {
      method: "POST",
      data: {
        plan,
        successUrl: `${window.location.origin}/billing/success`,
        cancelUrl: `${window.location.origin}/billing/cancel`,
      },
    },
    token,
  });

  return result;
};

// ─── Step 1: Company Setup ────────────────────────────────────────────────────

const CompanySetupStep = ({ data, onChange }) => {
  const [subdomainStatus, setSubdomainStatus] = useState(null); // null | "checking" | "available" | "taken" | "error"
  const [checkTimeout, setCheckTimeout] = useState(null);

  const handleSubdomainChange = useCallback(
    (e) => {
      const raw = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
      onChange("subdomain", raw);
      setSubdomainStatus(raw ? "checking" : null);

      if (checkTimeout) clearTimeout(checkTimeout);
      if (!raw) return;

      const t = setTimeout(async () => {
        const { available, error } = await checkSubdomainAvailability(raw);
        if (error) setSubdomainStatus("error");
        else setSubdomainStatus(available ? "available" : "taken");
      }, 600);
      setCheckTimeout(t);
    },
    [checkTimeout, onChange]
  );

  const subdomainColor =
    subdomainStatus === "available"
      ? "success"
      : subdomainStatus === "taken"
      ? "error"
      : "default";

  const subdomainMessage =
    subdomainStatus === "available"
      ? "✓ Available"
      : subdomainStatus === "taken"
      ? "✗ Already taken"
      : subdomainStatus === "checking"
      ? "Checking…"
      : subdomainStatus === "error"
      ? "Could not verify"
      : "";

  return (
    <Stack gap={2.5}>
      <Stack>
        <SC.AuthTitle sx={{ fontSize: "1.4rem", fontWeight: 700 }}>
          Set up your company
        </SC.AuthTitle>
        <Typography variant="body2" color="text.secondary">
          This is how your FleetMD account will be identified.
        </Typography>
      </Stack>

      <Stack gap={0.5}>
        <SC.InputLabel htmlFor="companyName">Company Name *</SC.InputLabel>
        <OutlinedInput
          id="companyName"
          value={data.companyName}
          onChange={(e) => onChange("companyName", e.target.value)}
          placeholder="Acme Transportation LLC"
          size="small"
          fullWidth
        />
      </Stack>

      <Stack gap={0.5}>
        <SC.InputLabel htmlFor="subdomain">Subdomain *</SC.InputLabel>
        <OutlinedInput
          id="subdomain"
          value={data.subdomain}
          onChange={handleSubdomainChange}
          placeholder="acme"
          size="small"
          fullWidth
          endAdornment={
            subdomainStatus === "checking" ? (
              <InputAdornment position="end">
                <CircularProgress size={16} />
              </InputAdornment>
            ) : null
          }
        />
        {subdomainMessage && (
          <Typography
            variant="caption"
            color={subdomainStatus === "available" ? "success.main" : subdomainStatus === "taken" ? "error.main" : "text.secondary"}
          >
            {subdomainMessage}
          </Typography>
        )}
        {data.subdomain && (
          <Typography variant="caption" color="text.secondary">
            Your platform URL: <strong>{data.subdomain}.fleetmd.io</strong>
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

// ─── Step 2: Admin Account ────────────────────────────────────────────────────

const AdminAccountStep = ({ data, onChange }) => {
  return (
    <Stack gap={2.5}>
      <Stack>
        <SC.AuthTitle sx={{ fontSize: "1.4rem", fontWeight: 700 }}>
          Create your admin account
        </SC.AuthTitle>
        <Typography variant="body2" color="text.secondary">
          You'll use these credentials to log in to your FleetMD dashboard.
        </Typography>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
        <Stack gap={0.5} flex={1}>
          <SC.InputLabel htmlFor="fullName">Full Name *</SC.InputLabel>
          <OutlinedInput
            id="fullName"
            value={data.fullName}
            onChange={(e) => onChange("fullName", e.target.value)}
            placeholder="Jane Smith"
            size="small"
            fullWidth
          />
        </Stack>
      </Stack>

      <Stack gap={0.5}>
        <SC.InputLabel htmlFor="email">Email Address *</SC.InputLabel>
        <OutlinedInput
          id="email"
          type="email"
          value={data.email}
          onChange={(e) => onChange("email", e.target.value)}
          placeholder="jane@acme.com"
          size="small"
          fullWidth
        />
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
        <Stack gap={0.5} flex={1}>
          <SC.InputLabel htmlFor="password">Password *</SC.InputLabel>
          <OutlinedInput
            id="password"
            type="password"
            value={data.password}
            onChange={(e) => onChange("password", e.target.value)}
            placeholder="Min 8 characters"
            size="small"
            fullWidth
          />
        </Stack>
        <Stack gap={0.5} flex={1}>
          <SC.InputLabel htmlFor="confirmPassword">Confirm Password *</SC.InputLabel>
          <OutlinedInput
            id="confirmPassword"
            type="password"
            value={data.confirmPassword}
            onChange={(e) => onChange("confirmPassword", e.target.value)}
            placeholder="Repeat password"
            size="small"
            fullWidth
          />
        </Stack>
      </Stack>
      {data.password && data.confirmPassword && data.password !== data.confirmPassword && (
        <Alert severity="error" sx={{ py: 0.5 }}>
          Passwords do not match
        </Alert>
      )}

      {/* Plan selection */}
      <Stack gap={1}>
        <SC.InputLabel>Select a Plan *</SC.InputLabel>
        <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
          {PLANS.map((plan) => (
            <Card
              key={plan.id}
              variant="outlined"
              onClick={() => onChange("plan", plan.id)}
              sx={{
                flex: 1,
                cursor: "pointer",
                border: data.plan === plan.id ? "2px solid" : "1px solid",
                borderColor: data.plan === plan.id ? "primary.main" : "divider",
                borderRadius: 2,
                transition: "border-color 0.2s",
                position: "relative",
              }}
            >
              {plan.popular && (
                <Chip
                  label="Popular"
                  color="primary"
                  size="small"
                  sx={{ position: "absolute", top: 8, right: 8, fontSize: "0.7rem" }}
                />
              )}
              <CardContent sx={{ pb: "12px !important" }}>
                <Typography fontWeight={700} fontSize="1rem">
                  {plan.label}
                </Typography>
                <Typography fontWeight={800} fontSize="1.5rem" color="primary.main">
                  {plan.price}
                  <Typography component="span" variant="caption" color="text.secondary">
                    {plan.period}
                  </Typography>
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Stack gap={0.5}>
                  {plan.features.map((f) => (
                    <Stack key={f} direction="row" gap={0.5} alignItems="center">
                      <CheckCircleIcon sx={{ fontSize: 14, color: "success.main" }} />
                      <Typography variant="caption">{f}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};

// ─── Step 3: Payment ──────────────────────────────────────────────────────────

const PaymentStep = ({ data, onSubscribe, isLoading, error }) => {
  const selectedPlan = PLANS.find((p) => p.id === data.plan) || PLANS[0];

  return (
    <Stack gap={2.5} alignItems="center">
      <Stack alignItems="center">
        <SC.AuthTitle sx={{ fontSize: "1.4rem", fontWeight: 700 }}>
          Complete your subscription
        </SC.AuthTitle>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          You'll be redirected to Stripe's secure checkout to enter your payment details.
        </Typography>
      </Stack>

      <Card variant="outlined" sx={{ width: "100%", maxWidth: 420, borderRadius: 2 }}>
        <CardContent>
          <Typography fontWeight={700} fontSize="1.1rem" mb={1}>
            Order Summary
          </Typography>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography>{selectedPlan.label} Plan</Typography>
            <Typography fontWeight={700}>{selectedPlan.price}/mo</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography variant="body2" color="text.secondary">Company</Typography>
            <Typography variant="body2">{data.companyName}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography variant="body2" color="text.secondary">Subdomain</Typography>
            <Typography variant="body2">{data.subdomain}.fleetmd.io</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography variant="body2" color="text.secondary">Admin</Typography>
            <Typography variant="body2">{data.email}</Typography>
          </Stack>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" justifyContent="space-between">
            <Typography fontWeight={700}>Total today</Typography>
            <Typography fontWeight={700} color="primary.main">
              {selectedPlan.price}/month
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" mt={1} display="block">
            You can cancel or change your plan at any time from the billing portal.
          </Typography>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ width: "100%", maxWidth: 420 }}>
          {error}
        </Alert>
      )}

      <SC.ControlButton
        variant="contained"
        onClick={onSubscribe}
        disabled={isLoading}
        sx={{ minWidth: 260 }}
      >
        {isLoading ? <CircularProgress size={20} color="inherit" /> : "Subscribe Now →"}
      </SC.ControlButton>

      <Typography variant="caption" color="text.secondary" textAlign="center">
        🔒 Secure payment powered by Stripe. Your card info never touches our servers.
      </Typography>
    </Stack>
  );
};

// ─── Main OnboardingPage ──────────────────────────────────────────────────────

export const OnboardingPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    // Step 1
    companyName: "",
    subdomain: "",
    // Step 2
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    plan: "professional",
  });

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  }, []);

  const validateStep = (step) => {
    switch (step) {
      case 0:
        if (!formData.companyName.trim()) return "Company name is required";
        if (!formData.subdomain.trim()) return "Subdomain is required";
        if (!/^[a-z0-9-]+$/.test(formData.subdomain)) return "Subdomain can only contain lowercase letters, numbers, and hyphens";
        return null;
      case 1:
        if (!formData.fullName.trim()) return "Full name is required";
        if (!formData.email.trim()) return "Email is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return "Invalid email address";
        if (!formData.password || formData.password.length < 8) return "Password must be at least 8 characters";
        if (formData.password !== formData.confirmPassword) return "Passwords do not match";
        if (!formData.plan) return "Please select a plan";
        return null;
      default:
        return null;
    }
  };

  const handleNext = () => {
    const err = validateStep(activeStep);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setActiveStep((s) => s + 1);
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((s) => s - 1);
  };

  const handleSubscribe = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Step 1: Register the tenant
      const registerRes = await fetch(`${API_BASE}/api/v1/tenants/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyName: formData.companyName,
          subdomain: formData.subdomain,
          adminEmail: formData.email,
          adminPassword: formData.password,
          plan: formData.plan,
        }),
      });

      const registerData = await registerRes.json();

      if (!registerRes.ok) {
        throw new Error(registerData?.message || registerData?.error || "Failed to register company");
      }

      if (!registerData?.accessToken) {
        throw new Error("Registration succeeded but no access token was returned");
      }

      // Step 2: Create checkout session using tenant-scoped auth token from registration
      const checkoutResult = await createCheckoutSession({
        plan: formData.plan,
        token: registerData.accessToken,
      });

      if (checkoutResult?.error) {
        throw new Error(checkoutResult.error || "Failed to create checkout session");
      }

      const checkoutUrl = checkoutResult?.value?.url;
      if (!checkoutUrl) {
        throw new Error("No checkout URL returned from server");
      }

      window.location.href = checkoutUrl;
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SC.AuthFormContainer sx={{ maxWidth: "720px", width: "100%", alignSelf: "center" }}>
      <Stack gap={3}>
        {/* Header */}
        <Stack alignItems="center" gap={0.5}>
          <Typography variant="h5" fontWeight={800} color="primary.main">
            FleetMD
          </Typography>
          <Typography variant="body2" color="text.secondary">
            NEMT Dispatch Platform — Start your free trial
          </Typography>
        </Stack>

        {/* Stepper */}
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Error banner */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step content */}
        <Box>
          {activeStep === 0 && (
            <CompanySetupStep data={formData} onChange={handleChange} />
          )}
          {activeStep === 1 && (
            <AdminAccountStep data={formData} onChange={handleChange} />
          )}
          {activeStep === 2 && (
            <PaymentStep
              data={formData}
              onSubscribe={handleSubscribe}
              isLoading={isLoading}
              error={error}
            />
          )}
        </Box>

        {/* Navigation */}
        <Stack direction="row" justifyContent="space-between" mt={1}>
          <SC.ControlButton
            variant="outlined"
            onClick={handleBack}
            disabled={activeStep === 0 || isLoading}
            sx={{ visibility: activeStep === 0 ? "hidden" : "visible" }}
          >
            ← Back
          </SC.ControlButton>

          {activeStep < STEPS.length - 1 && (
            <SC.ControlButton variant="contained" onClick={handleNext}>
              Continue →
            </SC.ControlButton>
          )}
        </Stack>

        {/* Login link */}
        <Stack direction="row" justifyContent="center" gap={0.5}>
          <Typography variant="body2" color="text.secondary">
            Already have an account?
          </Typography>
          <SC.AuthLink to="/">Sign in</SC.AuthLink>
        </Stack>
      </Stack>
    </SC.AuthFormContainer>
  );
};

export default OnboardingPage;
